import { BaseApiService } from './base.service';
import type { JsonApiResponse } from './base.service';
import type { Angle } from '@/types/concept';
import type {
  ProjectAngle,
  ProjectAngleUploadResponse,
} from '@/types/project-angle';

export class AnglesService extends BaseApiService {
  async getAngle(angleId: string): Promise<Angle> {
    const response = await this.httpGet<JsonApiResponse<Angle>>(
      `/v1/angles/${angleId}`,
      undefined,
      {
        loading: { trackEndpoint: `angle.${angleId}` },
      }
    );
    return response.data;
  }

  async deleteAngle(angleId: string): Promise<void> {
    await this.httpDelete(`/v1/angles/${angleId}`, undefined, {
      loading: { trackEndpoint: 'angle.delete' },
    });
  }

  async restoreAngle(angleId: string): Promise<void> {
    await this.post(`/v1/angles/${angleId}/restore`);
  }

  async listAngles(): Promise<Angle[]> {
    const response = await this.httpGet<JsonApiResponse<Angle[]>>(
      '/v1/angles',
      undefined,
      {
        loading: { trackEndpoint: 'angles.list' },
      }
    );
    return response.data;
  }

  async updateSelectedRender(
    conceptId: string,
    conceptAngleId: string,
    selectedRenderId: string
  ): Promise<void> {
    const payload = {
      selected_render_id: selectedRenderId,
    };

    await this.patch(
      `/v1/concepts/${conceptId}/angles/${conceptAngleId}/selection`,
      payload,
      undefined,
      {
        loading: { trackEndpoint: 'angle.update-selection' },
      }
    );
  }

  async updateAngle(
    angleId: string,
    updates: { name?: string | null; description?: string | null }
  ): Promise<Angle> {
    const payload = this.formatJsonApiData('angle', updates);

    const response = await this.patch<JsonApiResponse<Angle>>(
      `/v1/angles/${angleId}`,
      payload,
      undefined,
      {
        loading: { trackEndpoint: 'angle.update' },
      }
    );
    return response.data;
  }

  // =====================
  // Project-level Angles
  // =====================

  /**
   * Get all angles for a project, ordered by `order` field
   */
  async listProjectAngles(projectId: string): Promise<ProjectAngle[]> {
    const response = await this.httpGet<JsonApiResponse<ProjectAngle[]>>(
      `/v1/projects/${projectId}/angles`,
      undefined,
      {
        loading: { trackEndpoint: `project-angles.list.${projectId}` },
      }
    );
    return response.data;
  }

  /**
   * Create a new angle for a project (returns upload policy)
   */
  async createProjectAngle(
    projectId: string,
    fileName: string,
    md5: string,
    contentType: string,
    width: number,
    height: number,
    aspectRatio: string
  ): Promise<ProjectAngleUploadResponse> {
    const payload = this.formatJsonApiData('angle', {
      file_name: fileName,
      md5,
      content_type: contentType,
      width,
      height,
      aspect_ratio: aspectRatio,
    });

    const response = await this.post<
      JsonApiResponse<ProjectAngleUploadResponse>
    >(`/v1/projects/${projectId}/angles`, payload, undefined, {
      loading: { trackEndpoint: `project-angles.create.${projectId}` },
    });
    return response.data;
  }

  /**
   * Confirm angle upload — hits the single canonical endpoint.
   *
   * The former project-scoped and concept-scoped variants were collapsed
   * server-side. The backend now runs a HeadObject check on R2 before
   * marking the angle confirmed and only then emits the `angles/created`
   * realtime event, so clients no longer need to guard against the 404
   * negative-cache race.
   */
  async confirmAngle(angleId: string): Promise<void> {
    await this.post(
      `/v1/angles/${angleId}/confirm`,
      undefined,
      undefined,
      {
        loading: { trackEndpoint: `angles.confirm.${angleId}` },
      }
    );
  }

  /**
   * Bulk-dismiss angles that finished validation as Invalid.
   * Called from the upload modal close handlers — fire-and-forget on the
   * caller side so a flaky network never blocks the dismissal UX. Server
   * filters by user_id + status=invalid, so passing already-Valid or
   * foreign IDs is a no-op rather than an error.
   */
  async dismissInvalidAngles(angleIds: string[]): Promise<void> {
    if (angleIds.length === 0) return;

    const payload = this.formatJsonApiData('angle', {
      angle_ids: angleIds,
    });

    await this.post('/v1/angles/dismiss-invalid', payload, undefined, {
      loading: { trackEndpoint: 'angles.dismiss-invalid' },
    });
  }

  /**
   * Set an angle as the main angle for the project
   */
  async setMainAngle(
    projectId: string,
    angleId: string
  ): Promise<ProjectAngle> {
    const response = await this.patch<JsonApiResponse<ProjectAngle>>(
      `/v1/projects/${projectId}/angles/${angleId}/main`,
      undefined,
      undefined,
      {
        loading: { trackEndpoint: `project-angles.set-main.${angleId}` },
      }
    );
    return response.data;
  }

  /**
   * Reorder project angles. First angle becomes main.
   */
  async reorderProjectAngles(
    projectId: string,
    angleIds: string[]
  ): Promise<ProjectAngle[]> {
    const payload = this.formatJsonApiData('angle-orders', {
      angle_ids: angleIds,
    });

    const response = await this.patch<JsonApiResponse<ProjectAngle[]>>(
      `/v1/projects/${projectId}/angles/reorder`,
      payload,
      undefined,
      {
        loading: { trackEndpoint: `project-angles.reorder.${projectId}` },
      }
    );
    return response.data;
  }
}

export const anglesService = new AnglesService();
